export type UserRole = 'admin' | 'user' | 'organization_user' | 'locations_user';

export class User {
  constructor(
    public readonly uid: string,
    public readonly email: string,
    public readonly name: string,
    public readonly role: UserRole,
    public readonly organizationId: string,
    public readonly organizationName: string,
    public readonly locationIds: string[] = [],
    public readonly createdAt: Date = new Date(),
    public readonly updatedAt: Date = new Date(),
    public readonly cguAccepted: boolean = false,
    public readonly cguAcceptedAt: Date | null = null
  ) {}

  static create(
    uid: string,
    email: string,
    name: string,
    organizationId: string,
    organizationName: string,
    role: UserRole = 'user',
    locationIds: string[] = [],
    cguAccepted: boolean = false,
    cguAcceptedAt: Date | null = null,
    createdAt?: Date,
    updatedAt?: Date
  ): User {
    return new User(
      uid,
      email,
      name,
      role,
      organizationId,
      organizationName,
      locationIds,
      createdAt || new Date(),
      updatedAt || new Date(),
      cguAccepted,
      cguAcceptedAt
    );
  }

  isAdmin(): boolean {
    return this.role === 'admin';
  }

  canAccessAllOrganizations(): boolean {
    return this.isAdmin();
  }

  canManageUsers(): boolean {
    return this.isAdmin();
  }

  canManageAlerts(): boolean {
    return this.isAdmin();
  }

  canManageSensors(): boolean {
    return this.isAdmin();
  }

  canManageLocations(): boolean {
    return this.isAdmin();
  }

  canAccessOrganization(organizationId: string): boolean {
    return this.isAdmin() || this.organizationId === organizationId;
  }

  hasCguAccepted(): boolean {
    return this.cguAccepted;
  }

  isLocationRestricted(): boolean {
    return this.role === 'locations_user';
  }

  canAccessLocation(locationId: string): boolean {
    if (this.isAdmin() || this.role === 'user' || this.role === 'organization_user') {
      return true;
    }
    if (this.role === 'locations_user') {
      return this.locationIds.includes(locationId);
    }
    return false;
  }

  acceptCgu(): User {
    return new User(
      this.uid,
      this.email,
      this.name,
      this.role,
      this.organizationId,
      this.organizationName,
      this.locationIds,
      this.createdAt,
      new Date(),
      true,
      new Date()
    );
  }
}